import { Outlet, Navigate } from "react-router-dom"
import { Droplets } from "lucide-react"
import { useAuthStore } from "@/store/auth.store"
import { APP_NAME, APP_STATEMENT, APP_SUBTITLE } from "@/lib/constants"
import { DemoBanner } from "@/components/layout/DemoBanner"
import { landingFor } from "@/features/auth/landing"

const ACCESS_LEVELS = [
  { level: "District", text: "All Corporations, Zilla Parishads and local bodies of the district, and their STPs" },
  { level: "Municipal Corporation", text: "The Corporation and its STPs" },
  { level: "Zilla Parishad", text: "The Zilla Parishad and its STPs" },
  { level: "Municipal Council / Nagar Panchayat", text: "The local body and its STPs" },
]

// The sign-in page names no district or organisation: those are shown only to accounts that may see them.
export function AuthLayout() {
  const me = useAuthStore((s) => s.me)

  if (me) {
    return <Navigate to={landingFor(me)} replace />
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DemoBanner />
      <div className="flex flex-1">
        {/* Left panel — branding */}
        <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] flex-col justify-between p-12 bg-sidebar border-r border-border relative overflow-hidden">
          <div className="relative z-10">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Droplets className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <div className="relative z-10 space-y-6">
            <div className="space-y-3">
              <h1 className="text-3xl font-bold uppercase tracking-tight text-foreground leading-tight">
                District &amp; Municipal
                <br />
                Water Intelligence Platform
              </h1>
              <p className="text-sm font-medium text-muted-foreground">{APP_SUBTITLE}</p>
              <p className="text-sm text-foreground leading-relaxed">{APP_STATEMENT}</p>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Access follows your jurisdiction
              </p>
              <ul className="grid grid-cols-1 gap-1">
                {ACCESS_LEVELS.map((a) => (
                  <li key={a.level} className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm">
                    <span className="font-medium text-foreground">{a.level}</span>
                    <span className="block text-xs text-muted-foreground">{a.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="relative z-10 text-xs text-muted-foreground">
            {APP_NAME} · Demonstration environment with synthetic data.
          </div>
        </div>

        {/* Right panel — form */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
